import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react"
import { Modal } from "../../../features/Modal/Modal"
import BaseApi from "../../../api/api"
import { IOrder } from "../../../app/reducers/ordersReducer"
import readXlsxFile, { ParsedObjectsResult } from "read-excel-file/web-worker"
import { Tip, TipType } from "../../Tip/Tip"
import { NotificationService } from "../../../features/NotificationService/NotificationService"
import {
  Box,
  Flex,
  Button,
  Text,
  Table,
  ScrollArea,
  IconButton,
  Card,
  Checkbox,
  TextField,
} from "@radix-ui/themes"
import { UploadIcon, Cross1Icon } from "@radix-ui/react-icons"

export const ExpToWbOrders = ({ orders }: { orders?: IOrder[] }) => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const [ordersWithExpDate, setOrdersWithExpDate] = useState<
    { id: number; article: string; expDate?: string }[] | null
  >(null)

  const onOk = useCallback(() => {
    if (ordersWithExpDate) {
      BaseApi.setWBExpiration(ordersWithExpDate)
        .then((response) => {
          NotificationService.showNotification({
            message: response.data?.message,
            level: (response.data?.success && "success") || "error",
          })
        })
        .catch((error) => {
          NotificationService.showNotification({
            message: error.response?.data?.message || "Ошибка при установке",
            level: "error",
            errorBody: error.response,
          })
        })
    }
  }, [ordersWithExpDate])

  return (
    <Box>
      <Button disabled={!orders?.length} onClick={() => setOpen(true)}>
        Установить срок годности товарам
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        content={
          <ExpToWbOrdersContent
            orders={orders || []}
            setOrdersWithExpDate={setOrdersWithExpDate}
          />
        }
        isOkDisabled={!ordersWithExpDate?.length}
        onOk={() => onOk()}
        okContent={"Установить"}
        header="Установить срок годности товарам c Катрен"
      />
    </Box>
  )
}

const ExpToWbOrdersContent = ({
  orders,
  setOrdersWithExpDate,
}: {
  orders: IOrder[]
  setOrdersWithExpDate: (
    orders: { id: number; article: string; expDate?: string }[] | null,
  ) => void
}) => {
  const [fileName, setFileName] = useState<string | null>(null)
  const [data, setData] = useState<
    { id: number; article: string; expDate: string | undefined }[] | null
  >(null)
  const [error, setError] = useState<string | null>(null)
  const [customDate, setCustomDate] = useState<string>("")
  const [useCustomDate, setUseCustomDate] = useState(false)
  const [parsedRows, setParsedRows] = useState<
    { exp: string; name: string }[] | null
  >(null)
  const [joinData, setJoinData] = useState<
    (IOrder & { name?: string })[] | null
  >(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const defaultDate = (() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() + 2)
    return d.toISOString().split("T")[0]
  })()

  const handler = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setFileName(file.name)
      formDataForUpdate(
        readXlsxFile<{ exp: string; name: string }>(file, {
          schema: ShemaFile,
        }),
      )
    }
  }

  const formDataForUpdate = async (
    parsedFilePromise: Promise<
      ParsedObjectsResult<{ exp: string; name: string }>
    >,
  ) => {
    const { success, items } = await BaseApi.getWBKatrenRecode()
    if (success && parsedFilePromise) {
      if (!items.length) {
        setError("Файл перекодировки Катрен для WB пуст или не загружен")
        return
      }
      const enriched = orders.map((order) => {
        const recodeItem = items.find(
          (item) =>
            item.GoodCode?.toLowerCase() === order.article?.toLowerCase(),
        )
        return { ...order, name: recodeItem?.GoodName }
      })
      const dataFromXslx = await parsedFilePromise
      const rows = dataFromXslx.rows || []

      if (!rows.length) {
        setError(
          "Файлы со сроками годности пуст или возникли ошибки при расшифровке",
        )
        return
      }
      setJoinData(enriched)
      setParsedRows(rows)
      setError(null)
    }
  }

  useEffect(() => {
    if (!parsedRows || !joinData) return

    const effectiveDate = useCustomDate ? customDate : ""

    const result = joinData.map((order) => {
      const recodeItem = parsedRows.find(
        (item) => item.name?.toLowerCase() === order.name?.toLowerCase(),
      )
      return {
        id: order.id,
        article: order.article,
        expDate: recodeItem?.exp || effectiveDate || undefined,
      }
    })

    const withExpDate = result.filter((order) => !!order.expDate)

    if (!withExpDate.length) {
      setError(
        "Срок годности не сопоставлены с товарами, проверьте файлы со сроками и перекодировку",
      )
      setOrdersWithExpDate(null)
      setData(null)
      return
    }

    setError(null)
    setOrdersWithExpDate(withExpDate)
    setData(result)
  }, [parsedRows, joinData, customDate, useCustomDate, setOrdersWithExpDate])

  const daysLeft = customDate
    ? Math.ceil(
        (new Date(customDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      )
    : null

  if (error) {
    return (
      <Box>
        <Tip content={error} type={TipType.WARNING} />
      </Box>
    )
  }

  return (
    <Flex direction="column" gap="3" maxWidth="500px">
      <Flex direction="column" gap="2">
        {fileName ? (
          <Card size="1">
            <Flex align="center" justify="between">
              <Flex align="center" gap="2">
                <Text size="2" color="gray">
                  Файл:
                </Text>
                <Text size="2">{fileName}</Text>
              </Flex>
              <IconButton
                variant="ghost"
                color="red"
                size="1"
                onClick={() => {
                  setFileName(null)
                  setData(null)
                  setError(null)
                  setOrdersWithExpDate(null)
                  setParsedRows(null)
                  setJoinData(null)
                  if (fileInputRef.current) fileInputRef.current.value = ""
                }}
              >
                <Cross1Icon />
              </IconButton>
            </Flex>
          </Card>
        ) : (
          <>
            <Button onClick={() => fileInputRef.current?.click()} size="2">
              <UploadIcon />
              Загрузить файл c сроками годности
            </Button>
            <Tip
              type={TipType.INFO}
              content={
                <span>
                  Файл должен быть формата <b>XLSX</b> и содержать колонки:{" "}
                  <b>&quot;Срок годности&quot;</b> и <b>&quot;Название&quot;</b>
                </span>
              }
            />
          </>
        )}
        <input
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handler}
          type="file"
          accept=".xlsx"
        />
      </Flex>

      <Flex direction="column" gap="2">
        <Text as="label" size="2">
          <Flex gap="2" align="center">
            <Checkbox
              checked={useCustomDate}
              onCheckedChange={(checked) => {
                const enabled = checked === true
                setUseCustomDate(enabled)
                setCustomDate(enabled ? defaultDate : "")
              }}
            />
            Установить дату для ненайденных товаров
          </Flex>
        </Text>
        {useCustomDate && (
          <>
            <Tip
              type={TipType.INFO}
              content={
                <span>
                  Если товар не найден в файле — будет использована выбранная
                  дата.
                </span>
              }
            />
            <Flex gap="2" align="center">
              <Box
                style={{
                  border: "1px solid var(--gray-7)",
                  borderRadius: "var(--radius-2)",
                  padding: "var(--space-1) var(--space-2)",
                  background: "var(--color-surface)",
                  width: "fit-content",
                  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent-8)"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--gray-7)"
                  e.currentTarget.style.boxShadow = "none"
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent-8)"
                  e.currentTarget.style.boxShadow = "0 0 0 1px var(--accent-8)"
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "var(--gray-7)"
                  e.currentTarget.style.boxShadow = "none"
                }}
              >
                <TextField.Root
                  type="date"
                  size="2"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                />
              </Box>
              <span>
                Срок годности закончится через <b>{daysLeft}</b> дн.
              </span>
            </Flex>
          </>
        )}
      </Flex>

      {data && data.length > 0 && (
        <ScrollArea style={{ maxHeight: 300 }}>
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  Артикул продавца
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  Номер сборочного задания
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Срок годности</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.map((item) => (
                <Table.Row key={item.id}>
                  <Table.Cell>{item.article}</Table.Cell>
                  <Table.Cell>{item.id}</Table.Cell>
                  <Table.Cell>{item.expDate}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </ScrollArea>
      )}
    </Flex>
  )
}

const ShemaFile = {
  "Срок годности": {
    prop: "exp",
    type: String,
    required: true,
  },
  Название: {
    prop: "name",
    type: String,
    required: true,
  },
}
